import React from 'react';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import styled from 'styled-components';

import SorterIcon from 'common/Sort/SorterIcon';
import { StyledTable } from 'common/Table/StyledTable';
import FocusAreaTag from 'components/ActionPlans/FocusAreaTag';
import DetailsButton from 'components/lib/DetailsButton';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { ActionPlanCompanyFocusAreaRowType } from 'types/actionPlans';

import ActionPlansCompanyStatusCell from './ActionPlansCompanyStatusCell';
import ActionPlansCompanyTableWrapper from './ActionPlansCompanyTableWrapper';
import { SORT_ORDER } from '../../../constants';

const getColumnSortOrder = (ordering: string | undefined, field: string) => {
  if (ordering === field) {
    return SORT_ORDER.ASCEND;
  }
  if (ordering === `-${field}`) {
    return SORT_ORDER.DESCEND;
  }
  return null;
};

const getOrderingFromSorter = (sorter: any): string | undefined => {
  if (!sorter?.order || !sorter?.columnKey) {
    return undefined;
  }
  return sorter.order === SORT_ORDER.DESCEND ? `-${sorter.columnKey}` : sorter.columnKey;
};

const FocusAreaStyledTable = styled(StyledTable)`
  &&& {
    th.ant-table-column-sort,
    td.ant-table-column-sort {
      background: transparent;
    }
  }
`;

const CommunityName = styled.div`
  font-size: 14px;
  font-family: var(--font-regular);
  font-weight: 400;
  color: var(--text-primary);
`;

const PlanTitle = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const PlanTitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlanSourceIcon = styled.img`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
`;

const PlanTitleContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const OwnerName = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const DetailsCellContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  rows: ActionPlanCompanyFocusAreaRowType[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  ordering?: string;
  onOrderingChange: (ordering: string | undefined) => void;
};

const ActionPlansCompanyFocusAreaTable = ({
  rows,
  page,
  pageSize,
  total,
  onPageChange,
  ordering,
  onOrderingChange,
}: Props) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: 'COMMUNITY',
      dataIndex: 'community_name',
      key: 'community_name',
      width: 260,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'community_name'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (name: string) => (
        <CommunityName data-test-id="action-plans-company-focus-area-row-community-name">{name}</CommunityName>
      ),
    },
    {
      title: 'PLAN TITLE',
      dataIndex: 'name',
      key: 'name',
      width: 310,
      render: (name: string, row: ActionPlanCompanyFocusAreaRowType) => (
        <PlanTitleCell>
          <PlanSourceIcon
            data-test-id="action-plans-company-focus-area-row-source-icon"
            src={row.source === ACTION_PLAN_SOURCE.USER_ADDED ? '/icons/people-black.svg' : '/icons/ai-blob.svg'}
            alt=""
          />
          <PlanTitleContent>
            <PlanTitle data-test-id="action-plans-company-focus-area-row-plan-title">{name}</PlanTitle>
            <OwnerName data-test-id="action-plans-company-focus-area-row-owner">
              Owner: {row.owner?.name ?? 'Unassigned'}
            </OwnerName>
          </PlanTitleContent>
        </PlanTitleCell>
      ),
    },
    {
      title: 'FOCUS AREA',
      dataIndex: 'focus_area',
      key: 'focus_area',
      width: 220,
      render: (focusArea: string | null) => (
        <span data-test-id="action-plans-company-focus-area-row-focus-area">
          {focusArea ? <FocusAreaTag label={focusArea} /> : '-'}
        </span>
      ),
    },
    {
      title: 'DUE DATE',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'due_date'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (dueDate: string | null) => (
        <span data-test-id="action-plans-company-focus-area-row-due-date">
          {dueDate ? dayjs(dueDate).format('MMM D, YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'PLAN STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'status'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (status: number, row: ActionPlanCompanyFocusAreaRowType) => (
        <ActionPlansCompanyStatusCell
          status={status}
          overdueLabel={row.is_overdue ? 'Overdue' : undefined}
          dataTestId="action-plans-company-focus-area-row-status"
        />
      ),
    },
    {
      title: '',
      key: 'details',
      width: 90,
      render: (_: unknown, row: ActionPlanCompanyFocusAreaRowType) => (
        <DetailsCellContainer className="actions-hover-reveal">
          <DetailsButton
            data-test-id="action-plans-company-focus-area-row-details"
            onClick={() => navigate(`/community/${row.community_id}/action-plans/${row.id}`)}
          />
        </DetailsCellContainer>
      ),
    },
  ];

  return (
    <ActionPlansCompanyTableWrapper data-test-id="action-plans-company-focus-area-table">
      <FocusAreaStyledTable
        columns={columns as any}
        dataSource={rows}
        rowKey={((row: ActionPlanCompanyFocusAreaRowType) => row.id) as any}
        locale={{
          emptyText: <span data-test-id="action-plans-company-focus-area-table-empty">No data</span>,
        }}
        onChange={(_pagination: any, _filters: any, sorter: any) => {
          const nextOrdering = getOrderingFromSorter(sorter);
          if (nextOrdering !== ordering) {
            onOrderingChange(nextOrdering);
          }
        }}
        showSorterTooltip={false}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: page,
          pageSize,
          total,
          onChange: (pageNumber: number) => onPageChange(pageNumber),
          showTotal: (totalCount, range) => (
            <span>
              Showing {range[1] - range[0] + 1} of {totalCount} records
            </span>
          ),
        }}
        size="middle"
        bordered={false}
        tableLayout="fixed"
      />
    </ActionPlansCompanyTableWrapper>
  );
};

export default ActionPlansCompanyFocusAreaTable;
